import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAllFavouriteAssets, getProgramArchiveFavourites } from '../../../../../api/favorites';
import AltiboxAssetViewList from '../../../../../components/UI/AltiboxAssetViewList';
import { routes, swimlaneSettings } from '../../../../../config';
import {
  AltiboxAsset,
  AltiboxAssetDisplayTypes,
  Channel,
  Program,
  ProgramContentDetailResponse,
  RootState,
  VodAsset,
  VodAssetDisplayMode,
  VodListResponse,
} from '../../../../../interfaces';
import { Layout } from '../../../../../queries/ats/layout/types';
import { hasPicture } from '../../../../../utils/altiboxassetUtils';
import { classes } from '../../../../../utils/commonUtils';
import { getSearchCatchupAsset as getFavCatchupAsset } from '../../../../../utils/programArchiveUtils';
import { getSvodPicture } from '../../../../../utils/svodUtil';
import { addBadgeOnOwnedSeriesAssets, getVodPicture } from '../../../../../utils/vodUtils';
import styles from './favorites.module.scss';

interface FavoriteProps extends React.HTMLAttributes<HTMLElement> {
  view: Layout.SectionView;
  data: Layout.SectionItem[];
  className?: string;
}

export function Favorites({ view, data, className }: FavoriteProps) {
  if (data.length === 0) {
    return null;
  }

  return <UnmanagedFavorites view={view} className={className} />;
}

function UnmanagedFavorites({ view, className }: Pick<FavoriteProps, 'view' | 'className'>) {
  const channels = useSelector((state: RootState) => state.channelsReducer.channels);
  const isGuest = useSelector((state: RootState) => state.authReducer.isGuest);
  const isHome = useSelector((state: RootState) => state.authReducer.isHome);
  const myContent = useSelector((state: RootState) => state.vodReducer.myContent);
  const mySeries = useSelector((state: RootState) => state.vodReducer.mySeries);
  const loggedInWithCredentials = useSelector((state: RootState) => state.authReducer.loggedInWithCredentials);
  const isGuestUser = Boolean(isGuest || (isHome && !loggedInWithCredentials));

  const [combinedFavorites, setCombinedFavorites] = useState<AltiboxAsset[]>([]);

  useMemo(() => {
    function prepareVods(vods: VodAsset[]) {
      let assets = [...vods];

      if (myContent) {
        assets = addBadgeOnOwnedSeriesAssets(assets, myContent);
      }
      if (mySeries) {
        assets = addBadgeOnOwnedSeriesAssets(assets, mySeries!);
      }

      return assets.map((asset) => {
        return composeFavoriteVodAsset(asset, true);
      });
    }

    (async () => {
      let allVodFavs: VodAsset[] = [];
      let combinedFavs: AltiboxAsset[] = [];

      await getProgramArchiveFavourites(channels)
        .then((res) => programArchiveFavouritesToCorrectFormat(res, channels))
        .then((assets) => {
          const programArchiveFavs = assets.filter((x) => (x.asset as Program).istvod === '1');
          combinedFavs.push(
            ...programArchiveFavs.map((asset) => {
              if (asset.detailsLink) {
                asset.detailsLink = routes.unmanaged.base + asset.detailsLink;
              }
              return asset;
            }),
          );
        });

      await getAllFavouriteAssets()
        .then((assetList) => {
          if ((assetList as VodListResponse).vodlist) {
            allVodFavs.push(...(assetList as VodListResponse).vodlist);
          }
        })
        .then(() => {
          const vods = prepareVods(allVodFavs).filter((x) => x.picture) as AltiboxAsset[];
          combinedFavs.push(...vods);
        });

      setCombinedFavorites(combinedFavs);
    })();
  }, [channels, myContent, mySeries]);

  if (isGuestUser || !combinedFavorites.length) {
    return null;
  }

  return (
    <div className={classes(styles.favoriteWrapper, className)}>
      <AltiboxAssetViewList
        assets={combinedFavorites.splice(0, swimlaneSettings.limit - 1)}
        title={view.label}
        displayType={AltiboxAssetDisplayTypes.HorizontalScroller}
        titleLink={routes.unmanaged.base + routes.mycontent.base}
        vodDisplayMode={VodAssetDisplayMode.SVOD}
        loggedInWithCredentials={loggedInWithCredentials}
        favouriteView={true}
        myContent={myContent}
      />
    </div>
  );
}

function programArchiveFavouritesToCorrectFormat(responsePlaybill: ProgramContentDetailResponse, channels: Channel[]) {
  return responsePlaybill.playbilllist.map((asset) => {
    if (asset.hasOwnProperty('seasonNum') && asset.hasOwnProperty('subNum')) {
      // @ts-ignore deletion of non-optional property
      delete asset.seasonNum;
      // @ts-ignore deletion of non-optional property
      delete asset.subNum;
    }
    return getFavCatchupAsset(asset, channels);
  });
}

function composeFavoriteVodAsset(vodAsset: VodAsset, landscape?: boolean) {
  const { unmanaged, vod } = routes;
  let picture = null;

  if (landscape) {
    picture = hasPicture(vodAsset.picture) ? getSvodPicture(vodAsset) : '';
  } else {
    picture = hasPicture(vodAsset.picture) ? getVodPicture(vodAsset) : '';
  }

  let assetURL = unmanaged.base + vod.base + vod.details + '/' + vodAsset.externalContentCode;
  let percentWatched = 0;

  return {
    id: vodAsset.id,
    progress: percentWatched,
    description: vodAsset.introduce,
    type: vodAsset.type,
    hideBrandLogo: landscape ? false : true,
    picture: picture,
    coverImage:
      hasPicture(vodAsset.picture) && vodAsset.picture.background ? vodAsset.picture.background.split(',').pop() : '',
    title: vodAsset.name,
    genre: vodAsset.genres ? vodAsset.genres.split(',')[0] : '',
    playLink: assetURL,
    weight: 1,
    detailsLink: assetURL,
    asset: vodAsset,
  };
}
